import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { GtmRegistrationType } from "@obg/gtm.types/adaptive";
import { ActionWithPayload } from "@obg/ngrx-utils";
import { CommonState, interfaceOf } from "@obg/ngx.common";
import { AppError, JurisdictionSelector } from "@obg/ngx.core";
import { TelemetryService } from "@obg/ngx.global.evo";
import { differenceInYears } from "date-fns";
import { head } from "lodash-es";
import { filter, map, take, tap, withLatestFrom } from "rxjs/operators";

import { REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE } from "../registration/personal-number/registration-with-personal-number.action";
import { RegistrationWithPersonalNumberCompletePayload } from "../registration/personal-number/registration-with-personal-number.model";
import { RegistrationWithPersonalNumberSelector } from "../registration/personal-number/registration-with-personal-number.selector";
import { REGISTRATION_ACTION_TYPE } from "../registration/registration.action";
import { RegistrationService } from "../registration/registration.service";
import { RegistrationFeatureState } from "../state";

import { RegistrationTelemetryEventName } from "./registration-telemetry.model";

@Injectable()
export class RegistrationWithPersonalNumberTelemetryEffect {
	registrationSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(REGISTRATION_ACTION_TYPE.registrationSuccess),
				withLatestFrom(this.store.select(this.registrationWithPersonalNumberSelector.getState())),
				map(([, x]) => x),
				filter((registrationState) => !!registrationState.registrationDetails && !!registrationState.token),
				take(1),
				withLatestFrom(this.store.select(JurisdictionSelector.get)),
				tap(([registrationState, jurisdiction]) => {
					const affiliateData = head(this.registrationService.getAffiliateMetaData())!;
					const details = registrationState.registrationDetails!;

					const event: Record<string, any> = {
						event: RegistrationTelemetryEventName.ConfirmedRegistration,
						HashedEmail: registrationState.hashedEmail || "Missing",
						GUID: registrationState.token!,
						PartnerID: affiliateData?.affiliate,
						RegistrationType: jurisdiction,
						RegistrationMethod: GtmRegistrationType.Regular,
					};

					if (interfaceOf<RegistrationWithPersonalNumberCompletePayload>(details, "person")) {
						const person = details.person;
						event.Age = differenceInYears(Date.now(), new Date(person.dateOfBirth)).toString();
						event.Sex = person.gender;
					}

					this.telemetryService.track(event);
				})
			),
		{ dispatch: false }
	);

	verifyPersonalNumberFail$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE.verifyPersonalNumberFail),
				map((x: ActionWithPayload<AppError>) => x.payload),
				withLatestFrom(this.store.select(JurisdictionSelector.get)),
				tap(([error, jurisdiction]) => {
					this.telemetryService.track({
						event: RegistrationTelemetryEventName.FailedRegistration,
						Error: error.code,
						RegistrationType: jurisdiction,
					});
				})
			),
		{ dispatch: false }
	);

	constructor(
		private actions$: Actions,
		private telemetryService: TelemetryService,
		private store: Store<RegistrationFeatureState & CommonState>,
		private registrationWithPersonalNumberSelector: RegistrationWithPersonalNumberSelector,
		private registrationService: RegistrationService
	) {}
}
